import { useState, useEffect, useRef } from 'react';
import { X, User, Building, DollarSign, FileText, UserPlus, Image as ImageIcon, ZoomIn } from 'lucide-react';
import { scrollToTop } from '../../hooks/useScrollToTop';

interface ApplicationViewModalProps {
  application: any;
  onClose: () => void;
  onUpdateStatus: (id: string, status: string, notes?: string) => void;
}

export default function ApplicationViewModal({ application, onClose, onUpdateStatus }: ApplicationViewModalProps) {
  const [status, setStatus] = useState(application.status);
  const [notes, setNotes] = useState(application.notes || '');
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [monthlyRent, setMonthlyRent] = useState('');
  const [tenantNotes, setTenantNotes] = useState('');
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Scroll to top when modal opens
  useEffect(() => {
    scrollToTop(modalRef.current);
  }, []);

  const handleUpdateStatus = () => {
    onUpdateStatus(application._id, status, notes);
    onClose();
  };

  const handleCreateTenant = async () => {
    if (!monthlyRent) return;
    
    setCreatingTenant(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/lease-applications/${application._id}/create-tenant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          monthlyRent: parseFloat(monthlyRent),
          notes: tenantNotes
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        alert('Tenant created successfully!');
        setShowCreateTenant(false);
        onClose();
      } else {
        alert(data.message || 'Failed to create tenant');
      }
    } catch (err) {
      alert('Failed to create tenant. Please try again.');
    } finally {
      setCreatingTenant(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-yellow-100 text-yellow-800';
      case 'under_review': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div ref={modalRef} className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Lease Application Details
            </h2>
            <p className="text-gray-600">Submitted on {new Date(application.submittedAt).toLocaleDateString()}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Application Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shop Information */}
              <div className="bg-light-gray rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Building className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-gray-900">Shop Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Shop Number:</span>
                    <p className="text-gray-900">{application.shopNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Floor:</span>
                    <p className="text-gray-900">{application.shopFloor || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Size:</span>
                    <p className="text-gray-900">{application.shopSize ? `${application.shopSize} sqm` : 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Purpose:</span>
                    <p className="text-gray-900">{application.shopPurpose || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Company Information */}
              <div className="bg-light-gray rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Building className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-gray-900">Company Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Company Name:</span>
                    <p className="text-gray-900">{application.Company?.CompanyName || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Business Type:</span>
                    <p className="text-gray-900">{application.Company?.BusinessType || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Email:</span>
                    <p className="text-gray-900">{application.Company?.EmailAddress || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Phone:</span>
                    <p className="text-gray-900">{application.Company?.PhoneNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Entity Type:</span>
                    <p className="text-gray-900">{application.Company?.EntityType || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">State of Incorporation:</span>
                    <p className="text-gray-900">{application.Company?.StateOfIncorporation || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Current Setup Images */}
              {application.Images && application.Images.length > 0 && (
                <div className="bg-light-gray rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ImageIcon className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-gray-900">Current Setup Images</h3>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {application.Images.map((imageUrl: string, index: number) => (
                      <div key={index} className="relative group cursor-pointer" onClick={() => setSelectedImage(imageUrl)}>
                        <img
                          src={imageUrl}
                          alt={`Setup ${index + 1}`}
                          className="w-full h-32 object-cover rounded border hover:shadow-lg transition-shadow"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded flex items-center justify-center">
                          <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Financial Information */}
              <div className="bg-light-gray rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-gray-900">Financial Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Gross Revenue (Annual):</span>
                    <p className="text-gray-900">${application.Financials?.GrossRevenue || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Total Expenses (Annual):</span>
                    <p className="text-gray-900">${application.Financials?.TotalExpenses || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Guarantors */}
              {application.LeaseGuarantee?.Guarantors && application.LeaseGuarantee.Guarantors.filter((g: string) => g.trim()).length > 0 && (
                <div className="bg-light-gray rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <UserPlus className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-gray-900">Guarantors</h3>
                  </div>
                  <div className="space-y-2">
                    {application.LeaseGuarantee.Guarantors.filter((g: string) => g.trim()).map((guarantor: string, index: number) => (
                      <div key={index} className="text-sm text-gray-900">
                        {index + 1}. {guarantor}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Applicant Information */}
              <div className="bg-light-gray rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-gray-900">Applicant Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Applicant Name:</span>
                    <p className="text-gray-900">{application.Consent?.ApplicantName || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Application Date:</span>
                    <p className="text-gray-900">{new Date(application.submittedAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Current Notes */}
              {application.notes && (
                <div className="bg-light-gray rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-gray-900">Previous Notes</h3>
                  </div>
                  <p className="text-gray-700 text-sm">{application.notes}</p>
                </div>
              )}
            </div>

            {/* Status Update Panel */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-gray-200 rounded-lg p-4 sticky top-6">
                <h3 className="font-semibold text-gray-900 mb-4">Update Application</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Status
                  </label>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(application.status)}`}>
                    {application.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="submitted">Submitted</option>
                    <option value="under_review">Under Review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Add notes about this application..."
                  />
                </div>

                <div className="space-y-2">
                  <button
                    onClick={handleUpdateStatus}
                    className="w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    Update Status
                  </button>
                  
                  {status === 'approved' && (
                    <>
                      <button 
                        onClick={() => setShowCreateTenant(true)}
                        className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <UserPlus className="h-4 w-4" />
                        Create Tenant
                      </button>
                      <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">
                        Send Lease Agreement
                      </button>
                    </>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2">Quick Actions</h4>
                  <div className="space-y-2 text-sm">
                    <button className="text-blue-600 hover:text-blue-800 block">
                      Send Email
                    </button>
                    <button className="text-blue-600 hover:text-blue-800 block">
                      Schedule Call
                    </button>
                    <button className="text-blue-600 hover:text-blue-800 block">
                      Download Application
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Tenant Modal */}
      {showCreateTenant && (
        <div className="fixed inset-0 bg-black/50 z-60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create Tenant</h3>
              <button
                onClick={() => setShowCreateTenant(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Rent *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={monthlyRent}
                  onChange={(e) => setMonthlyRent(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter monthly rent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={tenantNotes}
                  onChange={(e) => setTenantNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Additional notes for tenant creation..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowCreateTenant(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTenant}
                  disabled={creatingTenant || !monthlyRent}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingTenant ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/80 z-70 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <img
              src={selectedImage}
              alt="Setup image"
              className="w-full h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}